/**
 * V2.0.82 — Symbol drag state ownership extracted from FloorPlan2D.jsx.
 *
 * This is a "state consolidation" refactor, not a full event-handler
 * extraction. The state atoms that used to hang off `useState` calls
 * scattered across the top of FloorPlan2D now live here:
 *
 *   - `dragSymbol`   — a palette drop is in flight (dragging from the
 *                       "+ Symbols" pill onto the canvas)
 *   - `moveSymbol`   — an existing symbol is being repositioned
 *   - `resizeOp`     — a corner handle of an existing symbol is
 *                       being dragged to resize
 *   - `dragInFlightRef`      — any of the above is active; per-frame
 *                                previews route through this so
 *                                pointerup can flush a single PUT
 *   - `pendingBlueprintRef`  — latest preview blueprint, used by
 *                                pointerup to flush without a stale
 *                                closure
 *   - `persistSymbols(list)` — mutates `floor.symbols` on the current
 *                                floor via either the preview or
 *                                update channel depending on drag
 *                                state
 *
 * V2.0.86 — Added `handleResizeStart(e, sym, corner, ctx)`, the corner-
 * handle pointerdown handler that used to live in FloorPlan2D. It seeds
 * `resizeOp` with the anchor world-coord (opposite corner) and pushes an
 * undo snapshot before flagging drag-in-flight. `ctx.pointerToWorld` and
 * `ctx.getSymbol` are injected at call time because both close over the
 * parent's viewport + catalog state.
 *
 * The pointer-move + pointer-up branches for resize live in
 * `useCanvasPointer` (they consume `resizeOp` via the setters here), so
 * this hook now owns the FULL lifecycle of the resize interaction start.
 *
 * Rationale: this is the minimum viable extraction. It consolidates 5+
 * scattered `useState` / `useRef` calls and gives future extractions a
 * single "state per interaction" hook to plug into a `useCanvasPointer`
 * orchestrator later. Line count reduction is modest but the shape is
 * now testable in isolation.
 */
import { useCallback, useRef, useState } from "react";

export default function useSymbolDrag({
  blueprint,
  floorIndex,
  onUpdateBlueprint,
  onPreviewBlueprint,
  pushHistory,
}) {
  // ---- Drag state atoms ---------------------------------------------
  const [dragSymbol, setDragSymbol] = useState(null); // palette drop: { type, w_m, h_m }
  const [moveSymbol, setMoveSymbol] = useState(null); // move existing: { id, startX, startZ, pointerStart }
  const [resizeOp, setResizeOp] = useState(null); // resize existing: { id, corner, startW_m, ... }

  // ---- Drag-in-flight refs ------------------------------------------
  // While ANY of the above is active, per-frame updates route through
  // the preview channel; a single PUT is flushed on pointerup.
  const dragInFlightRef = useRef(false);
  // Latest preview blueprint. `handleSvgPointerUp` reads this instead
  // of the closured `blueprint` prop (which lags by a render).
  const pendingBlueprintRef = useRef(null);

  const persistSymbols = useCallback(
    (newList) => {
      if (!blueprint) return;
      if (!dragInFlightRef.current) pushHistory?.();
      const next = JSON.parse(JSON.stringify(blueprint));
      const f = (next.floors || [])[floorIndex - 1];
      if (!f) return;
      f.symbols = newList;
      const handler = dragInFlightRef.current && onPreviewBlueprint
        ? onPreviewBlueprint
        : onUpdateBlueprint;
      if (dragInFlightRef.current) pendingBlueprintRef.current = next;
      handler?.(next);
    },
    [blueprint, floorIndex, onUpdateBlueprint, onPreviewBlueprint, pushHistory],
  );

  // V2.0.86 — Symbol resize start extracted from FloorPlan2D.jsx.
  // Called on pointerdown on a corner-handle rect. The caller supplies
  // `pointerToWorld` (screen → world coords for the current SVG) and
  // `getSymbol` (catalog lookup for default dimensions) at call time
  // because both close over the parent's viewport + catalog state.
  const handleResizeStart = useCallback((e, sym, corner, { pointerToWorld, getSymbol }) => {
    e.stopPropagation();
    const world = pointerToWorld(e);
    if (!world) return;
    const cat = getSymbol(sym.type);
    const curW = sym.override_w_m || cat?.width_m || 1;
    const curD = sym.override_d_m || cat?.depth_m || 1;
    // Anchor is the OPPOSITE corner in world coords. e.g. corner "se" →
    // anchor = north-west = (sym.x, sym.z).
    const anchorWorld = {
      x_m: corner.includes("e") ? sym.x : sym.x + curW,
      z_m: corner.includes("s") ? sym.z : sym.z + curD,
    };
    // Snapshot for undo BEFORE drag-in-flight starts.
    pushHistory?.();
    dragInFlightRef.current = true;
    setResizeOp({
      id: sym.id,
      corner,
      startW_m: curW,
      startD_m: curD,
      startCx: sym.x,
      startCz: sym.z,
      anchorWorld,
    });
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* synthetic events */ }
  }, [pushHistory]);

  const resetAll = useCallback(() => {
    setDragSymbol(null);
    setMoveSymbol(null);
    setResizeOp(null);
    dragInFlightRef.current = false;
    pendingBlueprintRef.current = null;
  }, []);

  return {
    // state
    dragSymbol,
    setDragSymbol,
    moveSymbol,
    setMoveSymbol,
    resizeOp,
    setResizeOp,
    // refs
    dragInFlightRef,
    pendingBlueprintRef,
    // helpers
    persistSymbols,
    resetAll,
    handleResizeStart,
  };
}
